import { useDependencyInjector } from "@whatslly/commons/di";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect } from "react";
import styles from "./block-wap-chat.module.scss";
import { useTranslation } from "react-i18next";
import type { WhatsappSelectorsType } from "@whatslly/types/integrations/im/WhatsappGetSelectorsResponse";
import { IMUserStore } from "@whatslly/instant-messaging/IMUserStore";
import type { UserType } from "@whatslly/types/User";
import { WidgetCommands } from "@whatslly/commons/widgetCommands";
import { widgetClient } from "@whatslly/commons/widgetClient";
import { AsyncImage } from "@whatslly/ui-components/AsyncImage";
import { LOADER_IMG } from "@whatslly/commons/constants";

const blockWhatsappChatEventFunction = (e: KeyboardEvent, selectors?: WhatsappSelectorsType) => {
  if (!selectors) return;
  const whatsllyRoot = document.getElementById("whatslly-root");
  const whatsllyContainer = document.getElementById(selectors.block_wap_chat?.whatslly_container);
  const whatsllyTopBarContainer = document.getElementById(selectors.block_wap_chat?.whatslly_top_bar_container);
  const whatsappLeftSide = document.querySelector(selectors.block_wap_chat.side);
  const childToCheck = e.target as Node;

  if (
    !whatsllyContainer?.contains(childToCheck) &&
    !whatsllyTopBarContainer?.contains(childToCheck) &&
    !whatsllyRoot?.contains(childToCheck) &&
    !whatsappLeftSide?.contains(childToCheck) &&
    whatsappLeftSide !== childToCheck
  ) {
    e.preventDefault();
  }
};

const getEntityTypes = (user: UserType | undefined) => {
  const types: { type: "contact" | "lead" | "person"; icon: "lead" | "contact" | "person-account" }[] = [];

  if (user?.createContacts) {
    types.push({ type: "contact", icon: "contact" });
  }

  if (user?.createLeads) {
    types.push({ type: "lead", icon: "lead" });
  }

  if (user?.createPersonAccount) {
    types.push({ type: "person", icon: "person-account" });
  }
  return types;
};

export const BlockWapChat: React.FC<{
  loading: boolean;
  isMultipleResults?: boolean;
  unsupportedGroup?: boolean;
  unknownGroupParticipants?: boolean;
}> = observer(({ loading, isMultipleResults, unsupportedGroup, unknownGroupParticipants }) => {
  const imUserStore = useDependencyInjector(IMUserStore);
  const { t } = useTranslation();

  const handleBlockWhatsappChatEventFunction = useCallback((e: KeyboardEvent) => {
    blockWhatsappChatEventFunction(e, imUserStore.selectors);
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleBlockWhatsappChatEventFunction, true);
    return () => {
      document.removeEventListener("keydown", handleBlockWhatsappChatEventFunction, true);
    };
  }, []);

  const createRecord = (type: "contact" | "lead" | "person") => {
    widgetClient.send(WidgetCommands.OPEN_CREATE_RECORD_PAGE, { type });
  };

  if (loading) {
    return <BlockWapChatLoadingOverlay>{t("loading_contact")}</BlockWapChatLoadingOverlay>;
  }

  if (unsupportedGroup) {
    return (
      <div className={styles.blockWapChatWrapper}>
        <span className={styles.warningEmoji}>⚠️</span>
        <div className={styles.header}>
          <h1>{t("unknown_group")}</h1>
        </div>
        <div className={styles.text}>
          <p>{t("unknown_group_actions_are_disabled")}</p>
        </div>
      </div>
    );
  }

  if (unknownGroupParticipants) {
    return (
      <div className={styles.blockWapChatWrapper}>
        <span className={styles.warningEmoji}>⚠️</span>
        <div className={styles.header}>
          <h1>{t("unknown_group_participants")}</h1>
        </div>
        <div className={styles.text}>
          <p>{t("unknown_group_participants_actions_are_disabled")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.blockWapChatWrapper}>
      <span className={styles.warningEmoji}>⚠️</span>
      <div className={styles.header}>
        <h1>{isMultipleResults ? t("multiple_records") : t("unknown_contact")}</h1>
      </div>
      {isMultipleResults ? (
        <div className={styles.text}>
          <p>{t("multiple_records_actions_are_disabled")} </p> <p>{t("multiple_records_to_enable_actions")}</p>
        </div>
      ) : (
        <div className={styles.text}>
          <p>{t("unknown_contact_actions_are_disabled")} </p> <p>{t("unknown_contact_to_enable_actions")}</p>
        </div>
      )}

      <div className={styles.actionsContainer}>
        {getEntityTypes(imUserStore.user).map(({ type, icon }, index) => {
          return (
            <React.Fragment key={index}>
              {index !== 0 && <span className={styles.actionsDevider} />}
              <BlockWapAction onClick={() => createRecord(type)} type={icon} />
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
});

const BlockWapAction: React.FC<{ type: "lead" | "contact" | "person-account"; onClick: () => void }> = ({ type, onClick }) => {
  const { t } = useTranslation();
  return (
    <div onClick={onClick} className={styles.action}>
      <div className={styles.actionSvgContainer}>
        <SvgV2 iconName={`add-new-${type}`} />
      </div>
      {t("create_type", { type: t(type) || type })}
    </div>
  );
};

const BlockWapChatLoadingOverlay = React.memo(({ children }: { children: React.ReactNode }) => {
  return (
    <div className={styles.blockWapChatWrapper} data-testid="block-message-overlay">
      <AsyncImage alt="tuvis loader" src={LOADER_IMG} />
      <p className={styles.loadingContact}>{children}</p>
    </div>
  );
});
